import { useEffect, useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { useAuth } from '@/auth/AuthProvider'

/**
 * Account menu — avatar in the top right, the usual place.
 *
 * Settings and Sign out live in here rather than as a nav tab and a loose
 * button. Neither is something you navigate to while working: one is visited
 * a handful of times ever, the other ends the session. Putting them behind the
 * avatar leaves the tab bar for the five screens that are actually part of
 * filing a receipt.
 */
export function UserMenu() {
  const { state, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node) === true) return
      setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (state.status !== 'signed-in') return null

  const { user } = state
  const email = user.email ?? ''
  const name = user.displayName ?? email

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${name}`}
        className="flex size-9 items-center justify-center overflow-hidden rounded-full border border-slate-300 bg-slate-100 transition hover:ring-2 hover:ring-slate-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
      >
        <Avatar photoUrl={user.photoURL} name={name} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-11 z-50 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
        >
          <div className="border-b border-slate-100 px-4 py-3">
            <p className="truncate text-sm font-medium text-slate-900">{name}</p>
            {email !== '' && email !== name && (
              <p className="truncate text-xs text-slate-500">{email}</p>
            )}
          </div>

          {/* Budget codes moved out of the main nav: they are set up once a
              year and edited rarely, so a permanent tab spent room the daily
              screens needed. Alongside Settings, which is the same kind of
              thing — configuration, not work. */}
          <NavLink
            to="/codes"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50"
          >
            Budget codes
          </NavLink>

          <NavLink
            to="/settings"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50"
          >
            Settings
          </NavLink>

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              void signOut()
            }}
            className="block w-full px-4 py-2.5 text-left text-sm text-slate-700 transition hover:bg-slate-50"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Google's profile photo, with initials behind it.
 *
 * The photo is loaded from googleusercontent.com, which returns 403 often
 * enough — rate limiting, or an account with no public photo — that a broken
 * image icon in the header is a real outcome rather than a theoretical one.
 * Falling back to initials keeps the control looking deliberate either way.
 */
function Avatar({ photoUrl, name }: { photoUrl: string | null; name: string }) {
  const [failed, setFailed] = useState(false)

  if (photoUrl === null || photoUrl === '' || failed) {
    return (
      <span className="text-sm font-semibold text-slate-600">{initials(name)}</span>
    )
  }

  return (
    <img
      src={photoUrl}
      alt=""
      referrerPolicy="no-referrer"
      className="size-full object-cover"
      onError={() => setFailed(true)}
    />
  )
}

function initials(name: string): string {
  const words = name
    .replace(/@.*$/, '')
    .split(/[\s._-]+/)
    .filter(Boolean)
  const letters = words.slice(0, 2).map((word) => word[0] ?? '')
  return letters.join('').toUpperCase() || '?'
}
